import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => {
    return articles.find((a) => a.slug === slug);
  }, [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <SEO
          title="文章不存在 - 黑料社区"
          description="抱歉，您访问的文章不存在或已被移除。"
          keywords="黑料社区"
        />
        <main>
          <Navbar />
          <div className="pt-24 md:pt-28 pb-14 md:pb-20">
            <div className="container-custom text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-file-unknow-line text-2xl"></i>
              </div>
              <h1 className="mt-4 text-xl font-heading font-semibold text-foreground-900">文章不存在</h1>
              <p className="mt-2 text-sm text-foreground-500">抱歉，您访问的文章不存在或已被移除。</p>
              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          </div>
          <Footer />
        </main>
      </>
    );
  }

  const categoryName = getCategoryName(article.categorySlug);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '黑料社区',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://heiliaoshequ.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEO
        title={`${article.title} - 黑料社区`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},黑料社区,${categoryName}`}
        canonicalUrl={`https://heiliaoshequ.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.imageUrl}
        articlePublishedTime={article.date}
        articleModifiedTime={article.date}
      />
      <SchemaOrg type="Article" data={articleSchema} />

      <main>
        <Navbar />
        <article className="pt-24 md:pt-28 pb-14 md:pb-20">
          <div className="container-custom">
            <div className="max-w-[860px] mx-auto">
              <div className="mb-6">
                <Breadcrumb
                  items={[
                    { label: '首页', to: '/' },
                    { label: categoryName, to: `/category/${article.categorySlug}` },
                    { label: article.title },
                  ]}
                />
              </div>

              <div className="mb-8">
                <div className="flex items-center gap-2 flex-wrap mb-4">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-2.5 py-1 bg-accent-100 text-accent-700 rounded text-xs font-medium hover:bg-accent-200 transition-colors cursor-pointer"
                  >
                    {categoryName}
                  </Link>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded text-xs font-medium">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight">
                  {article.title}
                </h1>

                <div className="mt-4 flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i> {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i> {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()} 阅读
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-chat-1-line"></i> {article.commentCount.toLocaleString()} 评论
                  </span>
                </div>
              </div>

              <div className="mb-10 rounded-lg overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-auto max-h-[480px] object-cover"
                />
              </div>

              <div
                className="prose-custom text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-14 md:mt-20 container-custom">
              <div className="max-w-[860px] mx-auto">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={ra.imageUrl}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3 md:p-4">
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {ra.title}
                        </h3>
                        <p className="mt-1 text-xs text-foreground-500">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </article>
        <Footer />
      </main>
    </>
  );
}